import React from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, DataGrid, Stack, Typography, FilterSelect, Tooltip } from '../../ui/core';
import { cardHeaderSx } from '../../ui/styles';
import { StationIcon, RouteIcon, InfoIcon, CalendarIcon } from '../../ui/icons';
import { useQuery, useCities, useStations, useStation, useFormatters } from '../../hooks';

import PartnerTypeChip from '../../components/widgets/Stations/PartnerTypeChip';
import StationDetailsDialog from '../../components/widgets/Stations/StationDetailsDialog';
import AddStation from '../../components/widgets/Stations/AddStation';
import StationBuilderLink from '../../components/widgets/Stations/StationBuilderLink';
import StationsSearch from '../../components/widgets/Stations/StationsSearch';

// Fixed page size rather than one derived from window.innerHeight — decouples
// the row-fetch size from how much chrome sits above the grid.
const PAGE_SIZE = 25;

const style = {
  dataGrid: {
    '.station--inoperational': {
      opacity: 0.6,
      backgroundColor: (theme) =>
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
      '&:hover, &.Mui-selected': {
        backgroundColor: (theme) => theme.palette.action.selected,
      },
    },
  },
};

/**
 * The only `sortBy` values `GET /admin/stations` accepts — `STATION_SORT_FIELDS`
 * in hitch-services `libs/dao/station`, allow-listed there because the value
 * lands in a Prisma `orderBy` key. Anything else is a 400, so an unrecognised
 * value in the URL is dropped rather than forwarded.
 */
const SORT_FIELDS = ['name', 'createdAt', 'updatedAt'];

/**
 * Which direction a column starts in on its FIRST click.
 *
 * Per-column, not a single global 'asc' (which is what the Vehicles catalog
 * uses, since all of its sortable columns are text). Ascending on a timestamp is
 * oldest-first, so a first click on Created would surface the oldest stations —
 * the exact opposite of the reason this column exists.
 *
 * These values MIRROR the server's own per-column defaults
 * (`StationsService.buildOrderBy`: `sortBy === 'name' ? 'asc' : 'desc'`), and
 * that agreement is the point rather than a coincidence. We always send an
 * explicit `sortDir`, so a mismatch would not show up as a broken request — it
 * would show up as the arrow in the header pointing the opposite way to the rows
 * underneath it.
 */
const DEFAULT_SORT_DIR = { name: 'asc', createdAt: 'desc', updatedAt: 'desc' };

const defaultSortDir = (field) => DEFAULT_SORT_DIR[field] ?? 'asc';

/**
 * A tooltip'd column-header icon.
 *
 * The `<Box component="span">` is required, not decoration: MUI's Tooltip
 * attaches a ref to its child, and every icon in `ui/icons` is a plain function
 * component (see `CalendarIcon`) that cannot hold one. Passing the icon directly
 * makes React warn "Function components cannot be given refs" and the tooltip
 * then never opens — a caveat that is invisible in the rendered output, which is
 * exactly why it is spelled out here.
 */
const HeaderHint = ({ title, children }) => (
  <Tooltip title={title}>
    <Box component="span" sx={{ display: 'flex', cursor: 'help' }}>
      {children}
    </Box>
  </Tooltip>
);

/**
 * Every URL this page pushes, in one place.
 *
 * The handlers below each used to build their own `URLSearchParams`, which meant
 * a new param had to be remembered in five separate places or it would be
 * dropped by whichever handler forgot it — paging would have silently reset the
 * sort. Each handler still decides WHICH params survive it (closing the details
 * dialog drops `stationId`; changing the city drops it too, and resets the page);
 * this only guarantees that what it names actually gets written.
 *
 * `fullScreen` is compared against undefined, not truthiness, because
 * `fullScreen=false` is a value this page writes on purpose.
 */
const buildSearch = ({ page, city, stationId, fullScreen, sortBy, sortDir }) => {
  const params = new URLSearchParams();
  params.set('page', page);
  if (city) params.set('city', city);
  if (stationId) params.set('stationId', stationId);
  if (fullScreen !== undefined) params.set('fullScreen', String(fullScreen));
  if (sortBy) params.set('sortBy', sortBy);
  if (sortBy && sortDir) params.set('sortDir', sortDir);
  return params.toString();
};

const StationsTable = () => {
  const history = useHistory();
  let { page, city, stationId, fullScreen, sortBy: rawSortBy, sortDir: rawSortDir } = useQuery();
  page = Math.max(+page || 1, 1);
  const offset = (page - 1) * PAGE_SIZE;

  // Sort state lives in the URL, like `city`, `page` and `stationId` — same
  // reasons: the writers are sibling components, and a link to "the stations
  // added most recently" is a thing an operator wants to paste to someone.
  const sortBy = SORT_FIELDS.includes(rawSortBy) ? rawSortBy : undefined;
  // A hand-edited or stale `sortDir` falls back to the column's own default
  // rather than to 'asc', so a link with `?sortBy=createdAt` alone still means
  // newest-first.
  const sortDir = !sortBy
    ? undefined
    : (rawSortDir === 'asc' || rawSortDir === 'desc' ? rawSortDir : defaultSortDir(sortBy));

  const { stations, total, isLoading } = useStations({
    city,
    page,
    pageSize: PAGE_SIZE,
    sortBy,
    sortDir,
  });
  const { cities } = useCities();
  const queryClient = useQueryClient();
  const f = useFormatters();

  /**
   * `createdAt`/`updatedAt` arrive as ISO-8601 STRINGS (JSON has no date type,
   * whatever `AdminStation` types them as), and are absent entirely until the
   * backend deploy carrying them reaches this environment. `toLocaleString`
   * parses the string and returns '' for a missing or unparseable value, which
   * becomes an em dash here — never 'Invalid Date', and never a thrown
   * `TypeError` that would take the whole grid down with it.
   */
  const timestamp = (value) =>
    f.date.toLocaleString(value, f.date.DateFormats.DATETIME_SHORT) || '—';

  const columns = [
    { field: 'id', hide: true },
    {
      field: 'name',
      headerName: 'Location',
      headerIcon: <StationIcon />,
      flex: 1,
      minWidth: 160,
      sortable: true,
    },
    { field: 'address', headerName: 'Address', headerIcon: <RouteIcon />, flex: 1.5, minWidth: 160 },
    {
      field: 'partnerType',
      headerName: 'Type',
      headerIcon: <InfoIcon />,
      width: 180,
      renderCell: ({ row }) => <PartnerTypeChip partnerType={row.partnerType} />,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      // The caveat is surfaced on hover rather than as a banner: it is real, but
      // it decays as new stations accumulate, and a permanent notice above the
      // grid would outlive its usefulness. No date is named in the copy on
      // purpose — the backfill ran at CURRENT_TIMESTAMP per environment, so
      // staging and production do not share one, and a hardcoded date would be
      // wrong in at least one of them. The copy says the value is not the real
      // creation date rather than that it was never recorded, because the
      // migration notes it is still recoverable from mongo_id (an ObjectID
      // encodes its own mint time) — just not from this column.
      headerIcon: (
        <HeaderHint title="Stations that existed before this column shipped all share one backfilled date, which is not their real creation date. Only stations added since show a true creation time.">
          <CalendarIcon />
        </HeaderHint>
      ),
      width: 170,
      sortable: true,
      renderCell: ({ row }) => timestamp(row.createdAt),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      headerIcon: (
        <HeaderHint title="Starts out equal to Created: edits made before this column shipped were never recorded, so this only reflects changes made since.">
          <CalendarIcon />
        </HeaderHint>
      ),
      width: 170,
      sortable: true,
      renderCell: ({ row }) => timestamp(row.updatedAt),
    },
  ];

  const rows = stations.map((d) => ({
    ...d,
    id: d.id,
    city: d.city,
  }));

  // Selection is URL-driven ON PURPOSE, not local state — this is the
  // faithful replacement for the old Redux `selection`, not a simplification
  // of it. `selection` was global specifically because the WRITERS of it are
  // not this component: `StationsSearch` dispatches `fetchSelectedStation`,
  // `AddStation` dispatches `setSelectedStation` after create, and
  // `updateStation`'s success action used to set `selection` directly so the
  // dialog's own edit fields (fed from this `station` prop) reflected a save.
  // A parent-local `useState` here is invisible to all three siblings/children
  // and would silently break each of them. Routing selection through
  // `?stationId=` and `useStation(stationId)` keeps it reachable by anyone who
  // can push a URL param, which is exactly what those three already do (or,
  // for the update case, what invalidating/refetching `['station', id]` would
  // do). Do not "simplify" this back to local state.
  const { station: selectedFromQuery } = useStation(stationId);
  const selected = selectedFromQuery ?? null;

  const onRowClick = ({ row }) => {
    // Seed the single-station cache from the row already on screen, keyed
    // exactly like useStation's own query key, so the dialog opens instantly
    // with this row's data instead of blanking while the id-driven query
    // above fetches it fresh. Preserves the "don't refetch on a row click"
    // intent without freezing the id itself.
    const clickedRow = stations.find((s) => s.id === row.id);
    if (clickedRow) {
      queryClient.setQueryData(['station', row.id], clickedRow);
    }

    history.push({
      search: buildSearch({ page, city, stationId: row.id, fullScreen: false, sortBy, sortDir }),
    });
  };

  const onCitySelected = (value) => {
    history.push({ search: buildSearch({ page: 1, city: value?.id, sortBy, sortDir }) });
  };

  const onPageChange = (p) => {
    history.push({ search: buildSearch({ page: p + 1, city, sortBy, sortDir }) });
  };

  const onDetailsClose = () => {
    history.push({ search: buildSearch({ page, city, sortBy, sortDir }) });
  };

  const onDetailsFullscreen = (fullScreen) => {
    history.push({ search: buildSearch({ page, city, stationId, fullScreen, sortBy, sortDir }) });
  };

  /**
   * Clicking the sorted column flips its direction; a new column starts at ITS
   * OWN default — descending for a timestamp, ascending for a name. This
   * deliberately differs from the Vehicles catalog's `toggleSort`, which starts
   * every new column ascending because all of its sortable columns are text.
   *
   * Back to page 1, like every other non-paging change: page 7 of a
   * name-ordered list is a different set of stations from page 7 of a
   * newest-first one, so keeping the page number would just show unrelated rows.
   */
  const onSortChange = (field) => {
    const nextDir = sortBy === field ? (sortDir === 'asc' ? 'desc' : 'asc') : defaultSortDir(field);
    history.push({ search: buildSearch({ page: 1, city, sortBy: field, sortDir: nextDir }) });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={cardHeaderSx}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em', flex: 1 }}>
            All Stations
          </Typography>
          <StationsSearch backdropTop={0} />
          <FilterSelect
            icon={<StationIcon />}
            label="City"
            placeholder="Filter by city"
            clearLabel="All cities"
            value={cities.find((c) => c.id === city) || null}
            options={cities}
            getOptionLabel={(c) => `${c.cityName}, ${c.stateAbbr}`}
            onChange={onCitySelected}
          />
          <StationBuilderLink />
          <AddStation />
        </Stack>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <DataGrid
            sx={style.dataGrid}
            rows={rows}
            columns={columns}
            getRowClassName={({ row }) => {
              if (!row.operational) {
                return 'station--inoperational';
              }
              return '';
            }}
            onRowClick={onRowClick}
            pageSize={PAGE_SIZE}
            loading={isLoading}
            paginationMode="server"
            // `total` (grand total across all pages), NOT `count` (rows in
            // this page alone) — DataGrid computes
            // `Math.ceil(rowCount / pageSize) || 1` for the page count, so
            // passing `count` (e.g. 25) would always yield 1 page and every
            // station past the first page would become unreachable, silently.
            // `total` is `null` on the search branch (no count query runs
            // there), and `Math.ceil(null / pageSize) || 1` collapses to 1
            // page just the same — so fall back to `offset + stations.length`
            // to keep the pager able to advance instead of pinning at page 1.
            rowCount={total ?? offset + stations.length}
            page={page - 1}
            onPageChange={onPageChange}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={onSortChange}
          />
        </Box>
      </Box>
      <StationDetailsDialog
        station={selected}
        fullScreen={fullScreen === 'true'}
        onClose={onDetailsClose}
        onFullscreen={onDetailsFullscreen}
      />
    </Box>
  );
};

export default StationsTable;
